import { useState } from 'react'
import { toast } from '@wiggui/ds/Toast'
import {
  useModalArchivePropertyStore,
  useModalDeletePropertyStore
} from '../../../store'
import {
  archiveProperties,
  deleteProperties,
  postRemovedReason
} from '../../../services'
import type {
  TPropertyBy,
  TPropertyReason,
  TPropertyRemoveReason
} from '../../../../interfaces'
import { useAmplitude } from '@wiggot/analytics'
import { useEventProperties } from '../../../../analytics/src/providers/posthog-provider/hooks/use-event-properties'

const assignedToastTextByType = {
  ARCHIVE: {
    error: {
      plural: 'archivaron tus propiedades',
      singular: 'archivo tu propiedad'
    },
    success: {
      plural: 'Tus propiedades se archivaron con éxito',
      singular: 'Tu propiedad se archivó con éxito'
    }
  },
  DELETE: {
    error: {
      plural: 'eliminaron tus propiedades',
      singular: 'elimino tu propiedad'
    },
    success: {
      plural: 'Tus propiedades se eliminaron con éxito',
      singular: 'Tu propiedad se elimino con éxito'
    }
  }
}

const checkboxsRadioInitialState: {
  id: number
  label: string
  isChecked: boolean
  deleteValue: TPropertyReason
  archiveValue: TPropertyReason
  removeReasonDelete: TPropertyRemoveReason
  removeReasonArchive: TPropertyRemoveReason
}[] = [
  {
    id: 1,
    label: 'Ya se vendió/rento',
    isChecked: false,
    deleteValue: 'ALREADY_TRADED',
    archiveValue: 'ALREADY_SOLD',
    removeReasonDelete: 'TRADED',
    removeReasonArchive: 'SOLD'
  },
  {
    id: 2,
    label: 'Perdí la autorización del cliente',
    isChecked: false,
    deleteValue: 'AUTHORIZATION_LOST',
    archiveValue: 'AUTHORIZATION_LOST',
    removeReasonDelete: 'AUTHORIZATION_LOST',
    removeReasonArchive: 'AUTHORIZATION_LOST'
  },
  {
    id: 3,
    label: 'Otro',
    isChecked: false,
    deleteValue: 'OTHER',
    archiveValue: 'OTHER',
    removeReasonDelete: 'OTHER',
    removeReasonArchive: 'OTHER'
  }
]

export const useHandleReasonStep = ({
  type
}: {
  type: 'DELETE' | 'ARCHIVE'
}) => {
  const { propertiesArchiveAction } = useEventProperties()
  const { trackEvent } = useAmplitude()
  const [isLoading, setIsLoading] = useState(false)
  const [textAreaValue, setTextAreaValue] = useState('')
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1)
  const [selectedByOption, setSelectedByOption] = useState<TPropertyBy | null>(
    null
  )
  const [dataCheckboxsRadio, setDataCheckboxsRadio] = useState(
    checkboxsRadioInitialState
  )

  const modalDeleteProperty = useModalDeletePropertyStore(
    (state) => state?.modalDeleteProperty
  )
  const modalArchiveProperty = useModalArchivePropertyStore(
    (state) => state?.modalArchiveProperty
  )

  const where = useModalArchivePropertyStore(
    (state) => state?.modalArchiveProperty?.where
  )

  const modalProperty =
    type === 'ARCHIVE' ? modalArchiveProperty : modalDeleteProperty

  const resetDeleteModal = useModalDeletePropertyStore((state) => state?.reset)
  const deleteOnSuccess = useModalDeletePropertyStore(
    (state) => state?.modalDeleteProperty?.onSuccess
  )
  const archiveOnSuccess = useModalArchivePropertyStore(
    (state) => state?.modalArchiveProperty?.onSuccess
  )
  const resetArchiveModal = useModalArchivePropertyStore(
    (state) => state?.reset
  )

  const onClose = () => {
    setTextAreaValue('')
    setDataCheckboxsRadio(checkboxsRadioInitialState)
    setIsLoading(false)
    setSelectedByOption(null)
    setCurrentStep(1)
    resetArchiveModal()
    resetDeleteModal()
  }

  const selectedOption = dataCheckboxsRadio?.find(({ isChecked }) => isChecked)

  const optionSoldIsChecked = dataCheckboxsRadio?.find(
    (item) =>
      item?.deleteValue === 'ALREADY_TRADED' ||
      item?.archiveValue === 'ALREADY_SOLD'
  )?.isChecked

  const textsToast = assignedToastTextByType[type]

  const handleArchiveProperties = async () => {
    if (currentStep === 1) {
      setCurrentStep(2)
      return
    }

    if (currentStep === 2 && selectedOption?.archiveValue === 'ALREADY_SOLD') {
      setCurrentStep(3)
      return
    }

    if (!selectedOption?.archiveValue) {
      return
    }

    try {
      setIsLoading(true)
      const { isSuccess } = await postRemovedReason({
        by: optionSoldIsChecked ? selectedByOption : null,
        event: 'REQUESTED_TO_DELETE',
        otherReason:
          optionOtherIsChecked && textAreaValue ? textAreaValue : null,
        propertiesIds: modalProperty?.propertiesIds,
        reason: selectedOption?.archiveValue
      })

      if (isSuccess) {
        const { isSuccess: isSuccessDeleteProperties } =
          await archiveProperties({
            otherReason:
              optionOtherIsChecked && textAreaValue ? textAreaValue : null,
            propertiesIds: modalProperty?.propertiesIds,
            removeReason: selectedOption?.removeReasonArchive
          })

        if (isSuccessDeleteProperties) {
          trackEvent({
            name: 'propertiesPerformanceStore_action',
            configs: {
              login: false,
              path: true
            }
          })
          propertiesArchiveAction(where)
          toast({
            type: 'success',
            title: propertiesLengthIsPlural
              ? textsToast.success.plural
              : textsToast.success.singular
          })
          archiveOnSuccess?.()
          onClose()
          return
        }
      }

      toast({
        type: 'error',
        title: `No se  ${
          propertiesLengthIsPlural
            ? textsToast.error.plural
            : textsToast.error.singular
        }`
      })
    } catch {
      toast({
        type: 'error',
        title: `No se  ${
          propertiesLengthIsPlural
            ? textsToast.error.plural
            : textsToast.error.singular
        }`
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteProperties = async () => {
    if (optionSoldIsChecked && currentStep === 1) {
      setCurrentStep(2)
      return
    }

    if (!selectedOption?.deleteValue) {
      return
    }

    try {
      setIsLoading(true)
      const { isSuccess } = await postRemovedReason({
        by: optionSoldIsChecked ? selectedByOption : null,
        event: 'REQUESTED_TO_DELETE',
        otherReason:
          optionOtherIsChecked && textAreaValue ? textAreaValue : null,
        propertiesIds: modalDeleteProperty?.propertiesIds,
        reason: selectedOption?.deleteValue
      })

      if (isSuccess) {
        const { isSuccess: isSuccessDeleteProperties } = await deleteProperties(
          {
            otherReason:
              optionOtherIsChecked && textAreaValue ? textAreaValue : null,
            propertiesIds: modalDeleteProperty?.propertiesIds,
            removeReason: selectedOption?.removeReasonDelete
          }
        )

        if (isSuccessDeleteProperties) {
          toast({
            type: 'success',
            title: propertiesLengthIsPlural
              ? textsToast.success.plural
              : textsToast.success.singular
          })
          onClose()
          deleteOnSuccess?.()
          return
        }
      }

      toast({
        type: 'error',
        title: `No se  ${
          propertiesLengthIsPlural
            ? textsToast.error.plural
            : textsToast.error.singular
        }`
      })
    } catch {
      toast({
        type: 'error',
        title: `No se  ${
          propertiesLengthIsPlural
            ? textsToast.error.plural
            : textsToast.error.singular
        }`
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleActionProperties = async () => {
    if (type === 'ARCHIVE') {
      handleArchiveProperties()
    }

    if (type === 'DELETE') {
      handleDeleteProperties()
    }
  }

  const allCheckboxsAreFalse = dataCheckboxsRadio?.every(
    ({ isChecked }) => !isChecked
  )

  const optionOtherIsChecked = dataCheckboxsRadio?.find(
    (item) => item?.archiveValue === 'OTHER' || item?.deleteValue === 'OTHER'
  )?.isChecked

  const propertiesLengthIsPlural =
    modalDeleteProperty?.propertiesIds?.length > 1

  const titleWithPropertyTitle = modalDeleteProperty?.title
    ? `¿Por qué ya no está disponible la propiedad "${modalDeleteProperty?.title}"?`
    : `¿Por qué ya no está disponible la propiedad${
        modalArchiveProperty?.title ? ` "${modalArchiveProperty?.title}"?` : '?'
      }`

  const title = propertiesLengthIsPlural
    ? `¿Por qué ya no están disponibles las ${
        modalDeleteProperty?.propertiesIds?.length ||
        modalArchiveProperty?.propertiesIds?.length
      } propiedades?`
    : titleWithPropertyTitle

  return {
    modalDeleteProperty,
    textAreaValue,
    setTextAreaValue,
    dataCheckboxsRadio,
    setDataCheckboxsRadio,
    allCheckboxsAreFalse,
    optionOtherIsChecked,
    title,
    isLoading,
    handleActionProperties,
    onClose,
    setSelectedByOption,
    selectedByOption,
    currentStep,
    optionSoldIsChecked,
    modalProperty
  }
}
